import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authenticatedFetch } from '../utils/api';
import styles from '../styles/Account.module.css';
import { useDocumentHead } from '../hooks/useDocumentHead';

const Account = () => {

  useDocumentHead(
    'Account Settings | TopSpeaks',
    'Manage your TopSpeaks account settings. Access support resources and control your privacy settings in one secure dashboard.',
    'index, follow'
  );

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      setIsDeleting(true);
      await authenticatedFetch('/api/auth/delete-account', {
        method: 'DELETE',
      });
      localStorage.removeItem(`assessment_completed_${user?.id}`);
      logout();
      navigate('/login');
    } catch (err) {
      setError('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  const DeleteConfirmationModal = () => {
    const isDeleteEnabled = deleteConfirmation === 'DELETE';

    return (
      <div className={`${styles.modalOverlay} ${showDeleteModal ? styles.modalShow : ''}`}>
        <div className={`${styles.modalContent} ${showDeleteModal ? styles.modalContentShow : ''}`}>
          <h2>Delete Account</h2>
          <p className={styles.modalWarning}>
            Warning: This action will permanently delete your account and all associated data.
          </p>
          <div className={styles.deletionList}>
            <div className={styles.deletionItem}>
              <span className={styles.deletionIcon}>⚠️</span>
              <div>
                <h4>Professional Profile</h4>
                <p>Your profile information and presentation preferences</p>
              </div>
            </div>
            <div className={styles.deletionItem}>
              <span className={styles.deletionIcon}>📊</span>
              <div>
                <h4>Presentation History</h4>
                <p>All recorded presentations and practice sessions</p>
              </div>
            </div>
            <div className={styles.deletionItem}>
              <span className={styles.deletionIcon}>📈</span>
              <div>
                <h4>Performance Analytics</h4>
                <p>Historical performance data and improvement metrics</p>
              </div>
            </div>
            <div className={styles.deletionItem}>
              <span className={styles.deletionIcon}>🤖</span>
              <div>
                <h4>AI Feedback History</h4>
                <p>Personalized AI insights and recommendations</p>
              </div>
            </div>
          </div>
          <div className={styles.confirmationSection}>
            <label htmlFor="deleteConfirm" className={styles.confirmText}>
              Type DELETE to confirm:
            </label>
            <input
              id="deleteConfirm"
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              className={styles.deleteInput}
              placeholder="Type DELETE"
              autoFocus
            />
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.modalButtons}>
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmation('');
                setError('');
              }}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              className={`${styles.deleteButton} ${!isDeleteEnabled ? styles.deleteButtonDisabled : ''}`}
              disabled={!isDeleteEnabled || isDeleting}
            >
              {isDeleting ? 'Deleting Account...' : 'Permanently Delete Account'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <main className={styles.content}>
        <div className={styles.header}>
          <div className={styles.userInfo}>
            <h1 className={styles.title}>{user?.fullName || 'Welcome'}</h1>
            <p className={styles.email}>{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className={styles.logoutButton}
            aria-label="Logout"
          >
            Sign Out
          </button>
        </div>

        <div className={styles.sectionGrid}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Presentation Settings</h2>
            <div className={styles.cardGrid}>
              <Link to="/assessment" className={styles.card}>
                <div className={styles.cardIcon}>🎯</div>
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>Presentation Goals</h3>
                  <p className={styles.cardDescription}>Update your main goals and success metrics</p>
                </div>
              </Link>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Resources & Support</h2>
            <div className={styles.cardGrid}>
              <Link to="/support" className={styles.card}>
                <div className={styles.cardIcon}>💬</div>
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>Contact Support</h3>
                  <p className={styles.cardDescription}>Get assistance from our professional team</p>
                </div>
              </Link>

              <Link to="https://topspeaks.com/docs" target="_blank" rel="noopener noreferrer" className={styles.card}>
                <div className={styles.cardIcon}>📚</div>
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>Documentation</h3>
                  <p className={styles.cardDescription}>Access guides and best practices</p>
                </div>
              </Link>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Legal & Privacy</h2>
            <div className={styles.cardGrid}>
              <Link to="/terms" className={styles.card}>
                <div className={styles.cardIcon}>📋</div>
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>Terms of Service</h3>
                  <p className={styles.cardDescription}>Review our terms and conditions</p>
                </div>
              </Link>

              <Link to="/privacy" className={styles.card}>
                <div className={styles.cardIcon}>🔒</div>
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>Privacy Policy</h3>
                  <p className={styles.cardDescription}>Learn about our data protection measures</p>
                </div>
              </Link>
            </div>
          </section>
        </div>

        <div className={styles.dangerZone}>
          <h2 className={styles.dangerTitle}>Danger Zone</h2>
          <button
            onClick={() => setShowDeleteModal(true)}
            className={styles.deleteAccountButton}
            aria-label="Delete account"
          >
            Delete Account
          </button>
        </div>
      </main>

      {showDeleteModal && <DeleteConfirmationModal />}
    </div>
  );
};

export default Account;